function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { createEditingStyle } from 'ol/style/Style';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import getDrawStyle from '../react-ol/utils/getDrawStyle';
import getFeatureStyle from '../react-ol/utils/getFeatureStyle';
import getStyleWithVertices from '../react-ol/utils/getStyleWithVertices';
import getMeasurementStyles from '../utils/getMeasurementStyles';
import { useGeometryArea } from './useGeometryArea';

/**
 * Takes geometry json style with all style types
 * Returns style types seperately in OpenLayers Style format
 * It also returns `getStyles` function which
 * Sets different style (measurements, vertices, shape render, default editing styles) for the given vector layer
 */
export default function useGeometryStyles(_ref) {
  var _style$render2, _style$highlight2, _style$select2, _style$render4;

  var {
    style,
    showVertices,
    showAreaMeasurements = false,
    showLineMeasurements = false,
    vectorLayer
  } = _ref;
  var renderStyle = useMemo(() => getFeatureStyle(_objectSpread({
    zIndex: 0
  }, style === null || style === void 0 ? void 0 : style.render)), [style === null || style === void 0 ? void 0 : style.render]);
  var highlightStyle = useMemo(() => getFeatureStyle(_objectSpread({
    zIndex: 1
  }, style === null || style === void 0 ? void 0 : style.highlight)), [style === null || style === void 0 ? void 0 : style.highlight]);
  var selectStyle = useMemo(() => getFeatureStyle(_objectSpread({
    zIndex: 2
  }, style === null || style === void 0 ? void 0 : style.select)), [style === null || style === void 0 ? void 0 : style.select]);
  var renderVerticesStyle = useMemo(() => {
    var _style$render;

    return getFeatureStyle(_objectSpread(_objectSpread({
      zIndex: 5
    }, style === null || style === void 0 ? void 0 : (_style$render = style.render) === null || _style$render === void 0 ? void 0 : _style$render.verticesStyle), {}, {
      displayVertices: showVertices
    }));
  }, [showVertices, style === null || style === void 0 ? void 0 : (_style$render2 = style.render) === null || _style$render2 === void 0 ? void 0 : _style$render2.verticesStyle]);
  var highlightVerticesStyle = useMemo(() => {
    var _style$highlight;

    return getFeatureStyle(_objectSpread(_objectSpread({}, style === null || style === void 0 ? void 0 : (_style$highlight = style.highlight) === null || _style$highlight === void 0 ? void 0 : _style$highlight.verticesStyle), {}, {
      displayVertices: showVertices
    }));
  }, [showVertices, style === null || style === void 0 ? void 0 : (_style$highlight2 = style.highlight) === null || _style$highlight2 === void 0 ? void 0 : _style$highlight2.verticesStyle]);
  var selectVerticesStyle = useMemo(() => {
    var _style$select;

    return getFeatureStyle(_objectSpread(_objectSpread({}, style === null || style === void 0 ? void 0 : (_style$select = style.select) === null || _style$select === void 0 ? void 0 : _style$select.verticesStyle), {}, {
      displayVertices: showVertices
    }));
  }, [showVertices, style === null || style === void 0 ? void 0 : (_style$select2 = style.select) === null || _style$select2 === void 0 ? void 0 : _style$select2.verticesStyle]);
  var measurementBaseStyle = useMemo(() => getFeatureStyle(_objectSpread({
    zIndex: 0
  }, style === null || style === void 0 ? void 0 : style.measurementBaseStyle)), [style === null || style === void 0 ? void 0 : style.measurementBaseStyle]);
  var measurementAreaStyle = useMemo(() => {
    var _style$measurementDra;

    return getFeatureStyle(_objectSpread({
      zIndex: 3
    }, style === null || style === void 0 ? void 0 : (_style$measurementDra = style.measurementDrawStyle) === null || _style$measurementDra === void 0 ? void 0 : _style$measurementDra.measurementAreaStyle));
  }, [style === null || style === void 0 ? void 0 : style.measurementDrawStyle]);
  var measurementLineStyle = useMemo(() => {
    var _style$measurementDra2;

    return getFeatureStyle(_objectSpread({
      zIndex: 4
    }, style === null || style === void 0 ? void 0 : (_style$measurementDra2 = style.measurementDrawStyle) === null || _style$measurementDra2 === void 0 ? void 0 : _style$measurementDra2.measurementLineStyle));
  }, [style === null || style === void 0 ? void 0 : style.measurementDrawStyle]);
  var renderStyles = useMemo(() => getStyleWithVertices(renderStyle, renderVerticesStyle, showVertices), [renderStyle, renderVerticesStyle, showVertices]);
  var highlightStyles = useMemo(() => getStyleWithVertices(highlightStyle, highlightVerticesStyle, showVertices), [highlightStyle, highlightVerticesStyle, showVertices]);
  var selectStyles = useMemo(() => getStyleWithVertices(selectStyle, selectVerticesStyle, showVertices), [selectStyle, selectVerticesStyle, showVertices]);
  var measurementBaseStyles = useMemo(() => getStyleWithVertices(measurementBaseStyle, renderVerticesStyle, showVertices), [measurementBaseStyle, renderVerticesStyle, showVertices]);
  var drawVerticesStyle = useMemo(() => {
    var _style$render3;

    return getFeatureStyle(_objectSpread(_objectSpread({}, style === null || style === void 0 ? void 0 : (_style$render3 = style.render) === null || _style$render3 === void 0 ? void 0 : _style$render3.verticesStyle), {}, {
      displayVertices: showVertices
    }));
  }, [showVertices, style === null || style === void 0 ? void 0 : (_style$render4 = style.render) === null || _style$render4 === void 0 ? void 0 : _style$render4.verticesStyle]);
  var drawStyle = useMemo(() => getDrawStyle(_objectSpread({
    zIndex: 3
  }, style === null || style === void 0 ? void 0 : style.draw), drawVerticesStyle), [drawVerticesStyle, style === null || style === void 0 ? void 0 : style.draw]);
  /**
   * `getStyleRef` contains a reference to the style function.
   *
   * OpenLayers isn't a React component, so if we used getCallback
   * here to create a new getStyles function whenever the dependencies
   * changed, we'd have to then assign that function to all the
   * features which currently had the old getStyles function
   * assigned to them.
   *
   * So instead, we create a reference to a function. The
   * function is recreated and reassigned to the reference whenever
   * the dependencies change. The getStyles function then calls
   * whatever funciton the reference is currently pointing at.
   *
   * This means that when a feature calls getStyles it will always
   * end up calling the most current version of the function.
   */

  var getStylesRef = useRef();
  var getStyles = useCallback((feature, resolution) => {
    var _getStylesRef$current;

    return (_getStylesRef$current = getStylesRef.current) === null || _getStylesRef$current === void 0 ? void 0 : _getStylesRef$current.call(getStylesRef, feature, resolution);
  }, []);
  var {
    getGeometryAreaWithSquareMetre
  } = useGeometryArea();
  useEffect(() => {
    var returnMeasurementStyles = (showLineMeasurements || showAreaMeasurements) && measurementBaseStyles && measurementAreaStyle && measurementLineStyle;

    getStylesRef.current = feature => {
      if (returnMeasurementStyles) {
        var geometry = feature.getGeometry();
        return getMeasurementStyles({
          areaStyle: measurementAreaStyle,
          lineStyle: measurementLineStyle,
          showAreaMeasurements,
          showLineMeasurements,
          shapeStyle: measurementBaseStyles,
          geometry,
          getArea: geom => getGeometryAreaWithSquareMetre(geom)
        });
      } else if (renderStyles) {
        return renderStyles;
      } else {
        return createEditingStyle();
      }
    }; // since our getStyles function has just changed, make sure
    // any styled features get redrawn.


    vectorLayer === null || vectorLayer === void 0 ? void 0 : vectorLayer.changed();
  }, [getGeometryAreaWithSquareMetre, highlightStyles, measurementAreaStyle, measurementBaseStyles, measurementLineStyle, renderStyles, showAreaMeasurements, showLineMeasurements, vectorLayer]);
  return {
    getStyles,
    measurementAreaStyle,
    measurementBaseStyles,
    renderStyle,
    renderStyles,
    measurementLineStyle,
    renderVerticesStyle,
    selectStyles,
    highlightStyles,
    drawStyle
  };
}